import React, { useState } from 'react';
import { Calendar, DollarSign, MapPin } from 'lucide-react';
interface Event {
  id: string;
  name: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
  };
  proposedEventDate: string;
  total: number;
  selections: {
    fullGen3Ring: number;
    isTwoDayEvent?: boolean;
  };
}
interface USMapProps {
  events: Event[];
  onEventClick?: (event: Event) => void;
}
export function USMap({
  events,
  onEventClick
}: USMapProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    x: 0,
    y: 0
  });
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  // Helper function to get actual ring count (divide by 2 for two-day events)
  const getActualRingCount = (event: Event) => {
    if (event.selections.isTwoDayEvent) {
      return Math.round(event.selections.fullGen3Ring / 2);
    }
    return event.selections.fullGen3Ring;
  };
  // Get unique months from events
  const getAvailableMonths = () => {
    const months = new Set<string>();
    events.forEach(event => {
      if (event.proposedEventDate) {
        const date = new Date(event.proposedEventDate);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months.add(monthYear);
      }
    });
    return Array.from(months).sort();
  };
  // Filter events by selected month
  const filteredEvents = selectedMonth === 'all' ? events : events.filter(event => {
    if (!event.proposedEventDate) return false;
    const date = new Date(event.proposedEventDate);
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return monthYear === selectedMonth;
  });
  // Format month for display
  const formatMonthDisplay = (monthYear: string) => {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };
  // Group events by state
  const eventsByState = filteredEvents.reduce((acc, event) => {
    // Try to get state from event venue location first, then fall back to shipping address
    let state = null;
    // First priority: eventVenueCityState (location field)
    if (event.customerInfo?.eventVenueCityState) {
      state = extractStateFromLocation(event.customerInfo.eventVenueCityState);
    }
    // Fallback: shipping address state
    if (!state && event.customerInfo?.state) {
      state = normalizeStateAbbreviation(event.customerInfo.state);
    }
    if (state) {
      if (!acc[state]) {
        acc[state] = {
          count: 0,
          rings: 0,
          events: []
        };
      }
      acc[state].count++;
      acc[state].rings += getActualRingCount(event);
      acc[state].events.push(event);
    }
    return acc;
  }, {} as Record<string, {
    count: number;
    rings: number;
    events: Event[];
  }>);
  console.log('Events by state:', eventsByState); // Debug log to see what's being calculated
  // Normalize state to 2-letter abbreviation
  function normalizeStateAbbreviation(state: string): string | null {
    const stateAbbreviations: Record<string, string> = {
      alabama: 'AL',
      al: 'AL',
      alaska: 'AK',
      ak: 'AK',
      arizona: 'AZ',
      az: 'AZ',
      arkansas: 'AR',
      ar: 'AR',
      california: 'CA',
      ca: 'CA',
      colorado: 'CO',
      co: 'CO',
      connecticut: 'CT',
      ct: 'CT',
      delaware: 'DE',
      de: 'DE',
      florida: 'FL',
      fl: 'FL',
      georgia: 'GA',
      ga: 'GA',
      hawaii: 'HI',
      hi: 'HI',
      idaho: 'ID',
      id: 'ID',
      illinois: 'IL',
      il: 'IL',
      indiana: 'IN',
      in: 'IN',
      iowa: 'IA',
      ia: 'IA',
      kansas: 'KS',
      ks: 'KS',
      kentucky: 'KY',
      ky: 'KY',
      louisiana: 'LA',
      la: 'LA',
      maine: 'ME',
      me: 'ME',
      maryland: 'MD',
      md: 'MD',
      massachusetts: 'MA',
      ma: 'MA',
      michigan: 'MI',
      mi: 'MI',
      minnesota: 'MN',
      mn: 'MN',
      mississippi: 'MS',
      ms: 'MS',
      missouri: 'MO',
      mo: 'MO',
      montana: 'MT',
      mt: 'MT',
      nebraska: 'NE',
      ne: 'NE',
      nevada: 'NV',
      nv: 'NV',
      'new hampshire': 'NH',
      nh: 'NH',
      'new jersey': 'NJ',
      nj: 'NJ',
      'new mexico': 'NM',
      nm: 'NM',
      'new york': 'NY',
      ny: 'NY',
      'north carolina': 'NC',
      nc: 'NC',
      'north dakota': 'ND',
      nd: 'ND',
      ohio: 'OH',
      oh: 'OH',
      oklahoma: 'OK',
      ok: 'OK',
      oregon: 'OR',
      or: 'OR',
      pennsylvania: 'PA',
      pa: 'PA',
      'rhode island': 'RI',
      ri: 'RI',
      'south carolina': 'SC',
      sc: 'SC',
      'south dakota': 'SD',
      sd: 'SD',
      tennessee: 'TN',
      tn: 'TN',
      texas: 'TX',
      tx: 'TX',
      utah: 'UT',
      ut: 'UT',
      vermont: 'VT',
      vt: 'VT',
      virginia: 'VA',
      va: 'VA',
      washington: 'WA',
      wa: 'WA',
      'west virginia': 'WV',
      wv: 'WV',
      wisconsin: 'WI',
      wi: 'WI',
      wyoming: 'WY',
      wy: 'WY'
    };
    const normalized = state.toLowerCase().trim();
    return stateAbbreviations[normalized] || null;
  }
  // Extract state from location string (e.g., "Los Angeles, CA" or "New York, NY")
  function extractStateFromLocation(location: string): string | null {
    if (!location) return null;
    const stateAbbreviations: Record<string, string> = {
      alabama: 'AL',
      al: 'AL',
      alaska: 'AK',
      ak: 'AK',
      arizona: 'AZ',
      az: 'AZ',
      arkansas: 'AR',
      ar: 'AR',
      california: 'CA',
      ca: 'CA',
      colorado: 'CO',
      co: 'CO',
      connecticut: 'CT',
      ct: 'CT',
      delaware: 'DE',
      de: 'DE',
      florida: 'FL',
      fl: 'FL',
      georgia: 'GA',
      ga: 'GA',
      hawaii: 'HI',
      hi: 'HI',
      idaho: 'ID',
      id: 'ID',
      illinois: 'IL',
      il: 'IL',
      indiana: 'IN',
      in: 'IN',
      iowa: 'IA',
      ia: 'IA',
      kansas: 'KS',
      ks: 'KS',
      kentucky: 'KY',
      ky: 'KY',
      louisiana: 'LA',
      la: 'LA',
      maine: 'ME',
      me: 'ME',
      maryland: 'MD',
      md: 'MD',
      massachusetts: 'MA',
      ma: 'MA',
      michigan: 'MI',
      mi: 'MI',
      minnesota: 'MN',
      mn: 'MN',
      mississippi: 'MS',
      ms: 'MS',
      missouri: 'MO',
      mo: 'MO',
      montana: 'MT',
      mt: 'MT',
      nebraska: 'NE',
      ne: 'NE',
      nevada: 'NV',
      nv: 'NV',
      'new hampshire': 'NH',
      nh: 'NH',
      'new jersey': 'NJ',
      nj: 'NJ',
      'new mexico': 'NM',
      nm: 'NM',
      'new york': 'NY',
      ny: 'NY',
      'north carolina': 'NC',
      nc: 'NC',
      'north dakota': 'ND',
      nd: 'ND',
      ohio: 'OH',
      oh: 'OH',
      oklahoma: 'OK',
      ok: 'OK',
      oregon: 'OR',
      or: 'OR',
      pennsylvania: 'PA',
      pa: 'PA',
      'rhode island': 'RI',
      ri: 'RI',
      'south carolina': 'SC',
      sc: 'SC',
      'south dakota': 'SD',
      sd: 'SD',
      tennessee: 'TN',
      tn: 'TN',
      texas: 'TX',
      tx: 'TX',
      utah: 'UT',
      ut: 'UT',
      vermont: 'VT',
      vt: 'VT',
      virginia: 'VA',
      va: 'VA',
      washington: 'WA',
      wa: 'WA',
      'west virginia': 'WV',
      wv: 'WV',
      wisconsin: 'WI',
      wi: 'WI',
      wyoming: 'WY',
      wy: 'WY'
    };
    const loc = location.toLowerCase().trim();
    // Check for state abbreviations or full state names in the location string
    for (const [key, value] of Object.entries(stateAbbreviations)) {
      if (loc.includes(key)) {
        return value;
      }
    }
    return null;
  }
  // Get color intensity based on ring count
  function getStateColor(rings: number): string {
    if (rings === 0) return '#f1f5f9'; // slate-100
    if (rings <= 2) return '#dbeafe'; // blue-100
    if (rings <= 5) return '#93c5fd'; // blue-300
    if (rings <= 10) return '#3b82f6'; // blue-500
    return '#1e40af'; // blue-800
  }
  // State positions (approximate center coordinates)
  const statePositions: Record<string, {
    x: number;
    y: number;
  }> = {
    AL: {
      x: 680,
      y: 520
    },
    AK: {
      x: 150,
      y: 650
    },
    AZ: {
      x: 220,
      y: 480
    },
    AR: {
      x: 580,
      y: 480
    },
    CA: {
      x: 120,
      y: 400
    },
    CO: {
      x: 340,
      y: 380
    },
    CT: {
      x: 880,
      y: 280
    },
    DE: {
      x: 850,
      y: 340
    },
    FL: {
      x: 780,
      y: 600
    },
    GA: {
      x: 740,
      y: 520
    },
    HI: {
      x: 280,
      y: 650
    },
    ID: {
      x: 220,
      y: 240
    },
    IL: {
      x: 620,
      y: 360
    },
    IN: {
      x: 680,
      y: 360
    },
    IA: {
      x: 560,
      y: 320
    },
    KS: {
      x: 480,
      y: 400
    },
    KY: {
      x: 700,
      y: 400
    },
    LA: {
      x: 580,
      y: 560
    },
    ME: {
      x: 900,
      y: 180
    },
    MD: {
      x: 830,
      y: 360
    },
    MA: {
      x: 890,
      y: 260
    },
    MI: {
      x: 680,
      y: 280
    },
    MN: {
      x: 540,
      y: 220
    },
    MS: {
      x: 620,
      y: 520
    },
    MO: {
      x: 580,
      y: 400
    },
    MT: {
      x: 300,
      y: 200
    },
    NE: {
      x: 440,
      y: 340
    },
    NV: {
      x: 180,
      y: 360
    },
    NH: {
      x: 890,
      y: 240
    },
    NJ: {
      x: 860,
      y: 320
    },
    NM: {
      x: 320,
      y: 480
    },
    NY: {
      x: 840,
      y: 260
    },
    NC: {
      x: 780,
      y: 460
    },
    ND: {
      x: 440,
      y: 200
    },
    OH: {
      x: 720,
      y: 360
    },
    OK: {
      x: 480,
      y: 460
    },
    OR: {
      x: 140,
      y: 220
    },
    PA: {
      x: 800,
      y: 320
    },
    RI: {
      x: 900,
      y: 280
    },
    SC: {
      x: 760,
      y: 500
    },
    SD: {
      x: 440,
      y: 260
    },
    TN: {
      x: 680,
      y: 460
    },
    TX: {
      x: 440,
      y: 540
    },
    UT: {
      x: 260,
      y: 360
    },
    VT: {
      x: 880,
      y: 230
    },
    VA: {
      x: 800,
      y: 400
    },
    WA: {
      x: 140,
      y: 160
    },
    WV: {
      x: 760,
      y: 380
    },
    WI: {
      x: 620,
      y: 260
    },
    WY: {
      x: 320,
      y: 280
    }
  };
  return <div className="space-y-6" data-id="element-2090">
      <div className="flex flex-col gap-4" data-id="element-2091">
        <div data-id="element-2092">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2" data-id="element-2093">
            Event Distribution Map
          </h2>
          <p className="text-sm text-slate-600" data-id="element-2094">
            Visual overview of events and ring counts across the United States.
            Hover over states to see event details.
          </p>
        </div>

        {/* Month Filter */}
        <div className="flex items-center gap-3" data-id="element-2095">
          <label className="text-sm font-medium text-slate-700" data-id="element-2096">
            Filter by Month:
          </label>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-2097">
            <option value="all" data-id="element-2098">All Months</option>
            {getAvailableMonths().map(month => <option key={month} value={month} data-id="element-2099">
                {formatMonthDisplay(month)}
              </option>)}
          </select>
          {selectedMonth !== 'all' && <span className="text-sm text-slate-600" data-id="element-2100">
              ({filteredEvents.length} event
              {filteredEvents.length !== 1 ? 's' : ''})
            </span>}
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm" data-id="element-2101">
          <div className="flex items-center gap-2" data-id="element-2102">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#dbeafe'
          }} data-id="element-2103"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-2104">1-2 rings</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2105">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#93c5fd'
          }} data-id="element-2106"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-2107">3-5 rings</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2108">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#3b82f6'
          }} data-id="element-2109"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-2110">6-10 rings</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2111">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{
            backgroundColor: '#1e40af'
          }} data-id="element-2112"></div>
            <span className="text-slate-600 whitespace-nowrap" data-id="element-2113">10+ rings</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-8 relative overflow-x-auto" data-id="element-2114">
        <svg viewBox="0 0 1000 700" className="w-full h-auto" onMouseLeave={() => setHoveredState(null)} data-id="element-2115">
          {/* Background */}
          <rect width="1000" height="700" fill="#f8fafc" data-id="element-2116" />

          {/* State markers */}
          {Object.entries(statePositions).map(([state, pos]) => {
          const data = eventsByState[state];
          const rings = data?.rings || 0;
          const count = data?.count || 0;
          const color = getStateColor(rings);
          // Calculate total rings for this state
          const totalRings = data ? data.events.reduce((sum, event) => sum + getActualRingCount(event), 0) : 0;
          return <g key={state} onMouseEnter={e => {
            if (totalRings > 0) {
              setHoveredState(state);
              const svg = e.currentTarget.ownerSVGElement;
              if (svg) {
                const rect = svg.getBoundingClientRect();
                const svgX = pos.x / 1000 * rect.width;
                const svgY = pos.y / 700 * rect.height;
                setTooltipPosition({
                  x: rect.left + svgX,
                  y: rect.top + svgY
                });
              }
            }
          }} style={{
            cursor: totalRings > 0 ? 'pointer' : 'default'
          }} data-id="element-2117">
                {/* State circle */}
                <circle cx={pos.x} cy={pos.y} r={totalRings > 0 ? Math.max(25, Math.min(60, totalRings * 3)) : 15} fill={color} stroke="#1e293b" strokeWidth={hoveredState === state ? '2' : '1'} opacity={totalRings > 0 ? hoveredState === state ? 1 : 0.9 : 0.3} style={{
              transition: 'all 0.2s ease'
            }} data-id="element-2118" />

                {/* State abbreviation */}
                <text x={pos.x} y={pos.y - 8} textAnchor="middle" fontSize="13" fontWeight="700" fill={totalRings > 5 ? '#ffffff' : '#1e293b'} style={{
              pointerEvents: 'none'
            }} data-id="element-2119">
                  {state}
                </text>

                {/* Total ring count ONLY - single text element */}
                {totalRings > 0 && <text x={pos.x} y={pos.y + 6} textAnchor="middle" fontSize="16" fontWeight="800" fill={totalRings > 5 ? '#ffffff' : '#1e40af'} style={{
              pointerEvents: 'none'
            }} data-id="element-2120">
                    {totalRings}R
                  </text>}
              </g>;
        })}
        </svg>

        {/* Tooltip */}
        {hoveredState && eventsByState[hoveredState] && <div className="fixed z-50 bg-white rounded-lg shadow-2xl border border-slate-200 p-3 sm:p-4 max-w-[90vw] sm:max-w-md" style={{
        left: `${Math.min(tooltipPosition.x + 20, window.innerWidth - 320)}px`,
        top: `${Math.max(20, tooltipPosition.y - 100)}px`,
        pointerEvents: 'none'
      }} data-id="element-2121">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3" data-id="element-2122">
              <h4 className="font-semibold text-slate-800 text-base sm:text-lg" data-id="element-2123">
                {hoveredState}
              </h4>
              <div className="text-xs sm:text-sm text-slate-600" data-id="element-2124">
                {eventsByState[hoveredState].count} event
                {eventsByState[hoveredState].count !== 1 ? 's' : ''} •{' '}
                {eventsByState[hoveredState].rings} ring
                {eventsByState[hoveredState].rings !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto" data-id="element-2125">
              {eventsByState[hoveredState].events.map(event => <div key={event.id} className="p-2 sm:p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border border-slate-200" onClick={e => {
            e.stopPropagation();
            if (onEventClick) {
              onEventClick(event);
            }
          }} style={{
            pointerEvents: 'auto'
          }} data-id="element-2126">
                  <div className="font-medium text-slate-800 mb-1 text-sm break-words" data-id="element-2127">
                    {event.eventName}
                  </div>
                  <div className="text-xs text-slate-600 space-y-1" data-id="element-2128">
                    <div className="flex items-center gap-1" data-id="element-2129">
                      <MapPin size={12} className="flex-shrink-0" data-id="element-2130" />
                      <span className="break-words" data-id="element-2131">{event.name}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1" data-id="element-2132">
                      <div className="flex items-center gap-1" data-id="element-2133">
                        <Calendar size={12} className="flex-shrink-0" data-id="element-2134" />
                        <span data-id="element-2135">
                          {event.proposedEventDate ? new Date(event.proposedEventDate).toLocaleDateString() : 'No date'}
                        </span>
                        {event.selections.isTwoDayEvent && <span className="text-purple-600 text-xs font-medium" data-id="element-2136">
                            (2d)
                          </span>}
                      </div>
                      <div className="flex items-center gap-1" data-id="element-2137">
                        <span className="font-semibold text-blue-600" data-id="element-2138">
                          {getActualRingCount(event)}R
                        </span>
                        <span className="text-slate-500" data-id="element-2139">•</span>
                        <DollarSign size={12} className="flex-shrink-0" data-id="element-2140" />
                        <span className="font-semibold" data-id="element-2141">
                          ${event.total.toFixed(0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
            <div className="mt-2 text-xs text-slate-500 text-center" data-id="element-2142">
              Click on an event to view full details
            </div>
          </div>}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-2143">
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-2144">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-2145">
            Total Events
          </div>
          <div className="text-xl sm:text-2xl font-bold text-slate-800" data-id="element-2146">
            {filteredEvents.length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-2147">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-2148">
            Total Rings
          </div>
          <div className="text-xl sm:text-2xl font-bold text-blue-600" data-id="element-2149">
            {filteredEvents.reduce((sum, e) => sum + getActualRingCount(e), 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-2150">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-2151">
            States Covered
          </div>
          <div className="text-xl sm:text-2xl font-bold text-green-600" data-id="element-2152">
            {Object.keys(eventsByState).length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4" data-id="element-2153">
          <div className="text-xs sm:text-sm text-slate-600 mb-1" data-id="element-2154">
            Avg Rings/Event
          </div>
          <div className="text-xl sm:text-2xl font-bold text-purple-600" data-id="element-2155">
            {filteredEvents.length > 0 ? (filteredEvents.reduce((sum, e) => sum + getActualRingCount(e), 0) / filteredEvents.length).toFixed(1) : '0'}
          </div>
        </div>
      </div>

      {/* State Breakdown Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-2156">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 bg-slate-50" data-id="element-2157">
          <h3 className="text-base sm:text-lg font-semibold text-slate-800" data-id="element-2158">
            State Breakdown
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-2159">
          <table className="w-full" data-id="element-2160">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2161">
              <tr data-id="element-2162">
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-2163">
                  State
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-2164">
                  Events
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-2165">
                  Rings
                </th>
                <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider" data-id="element-2166">
                  Event Names
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-2167">
              {Object.entries(eventsByState).sort((a, b) => b[1].rings - a[1].rings).map(([state, data]) => <tr key={state} className="hover:bg-slate-50" data-id="element-2168">
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap" data-id="element-2169">
                      <span className="font-semibold text-slate-800" data-id="element-2170">
                        {state}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-slate-600" data-id="element-2171">
                      {data.count}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right" data-id="element-2172">
                      <span className="font-semibold text-blue-600" data-id="element-2173">
                        {data.rings}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 text-sm text-slate-600" data-id="element-2174">
                      {data.events.map(e => e.eventName).join(', ')}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}